/**
 * Timbrica — Tic-tac-toe shared rules module.
 *
 * Used by BOTH halves of the game in resources/views/tools/tic-tac-toe.blade.php:
 * the referee (the code that decides a finished game) and the bots. Pure
 * functions only — no DOM, no globals beyond `TttRules`. Loadable via a
 * <script> tag (browser) or require()/vm (node tests).
 *
 * WHY THIS FILE EXISTS — the bug it was extracted to prevent:
 *
 *   Misère ("3 in a row LOSES") shipped as a boolean threaded into the bot's
 *   search and NOWHERE else. The referee kept the classic rule hardcoded, so it
 *   awarded the win to whoever completed a line. The bot therefore played
 *   correctly — avoiding three, trying to force three on the opponent — and the
 *   referee declared it the loser every time it succeeded. The mode's own hint
 *   text said "3 in a row LOSES" in all nine locales while the code scored the
 *   opposite.
 *
 *   Measured on production telemetry before the fix (2026-08-20): against the
 *   STRONGEST bot, humans won 10 of the 15 decisive misère games; 40 of 40
 *   players of the mode were affected. It was reported as "in Misère mode the
 *   bot always loses, because I make three in a row and I'm credited the win".
 *
 *   `invertGoal` is now the ONLY place the objective varies, and both halves
 *   read it from here. A variant that flips the goal costs one flag — not a
 *   parallel rules path. (Same shape as `giveaway` in public/js/checkers-rules.js.)
 *
 * Board representation: a 9-element array, index 0..8 row-major, each cell
 * 'X' | 'O' | null.
 */
(function (root) {
'use strict';

const WINNING_LINES = [
    [0,1,2],[3,4,5],[6,7,8],
    [0,3,6],[1,4,7],[2,5,8],
    [0,4,8],[2,4,6]
];
const LINES_THROUGH = (function(){
    const m = [[],[],[],[],[],[],[],[],[]];
    for (const line of WINNING_LINES) for (const i of line) m[i].push(line);
    return m;
})();

/* ──────────────── Variant table ────────────────
   The single owner of "what does completing a line mean" and "what is a sound
   first move". Anything that needs either answer asks here. */
const VARIANTS = {
    classic: {
        invertGoal: false,
        openingBook: [4, 0, 2, 6, 8]   // centre + corners: every opening draws
    },
    misere: {
        invertGoal: true,
        /* Centre and nothing else. Exhaustively solved — see
           tests/js/tic-tac-toe-rules.test.mjs "misère is drawn only from the
           centre": with perfect play misère 3×3 is a draw, but ONLY if the first
           player opens on cell 4; all eight other openings are theoretically
           LOST. Reusing the classic book here (centre + 4 corners, picked
           uniformly) walked a bot that moves first into a lost game 4 times
           out of 5, and no amount of search depth afterwards could recover it. */
        openingBook: [4]
    },
    ultimate: {
        /* Ultimate plays classic scoring on the big board; its extra rules are
           about WHERE you may move, not about what winning means. */
        invertGoal: false,
        openingBook: [4, 0, 2, 6, 8]
    }
};

function rulesFor(variant){ return VARIANTS[variant] || VARIANTS.classic; }
function other(mark){ return mark === 'X' ? 'O' : 'X'; }

/* ──────────────── Board helpers ──────────────── */
function isWin(board, p){
    for (const line of WINNING_LINES){
        if (board[line[0]]===p && board[line[1]]===p && board[line[2]]===p) return line;
    }
    return null;
}
function isFull(board){ for (let i=0;i<9;i++) if (board[i]===null) return false; return true; }
function emptyCells(board){ const e=[]; for (let i=0;i<9;i++) if (board[i]===null) e.push(i); return e; }

/* ──────────────── The referee ────────────────
   Given a board just after `justMoved` played, is the game over and who won?
   Returns null while the game continues, otherwise
     { kind: 'win'|'draw', winner, line, completedBy }
   `completedBy` is kept separate from `winner` on purpose: under misère they are
   opposite players, and the UI needs both — the line to draw, and the player to
   congratulate. */
function outcome(board, justMoved, variant){
    const R = rulesFor(variant);
    const line = isWin(board, justMoved);
    if (line){
        return {
            kind: 'win',
            winner: R.invertGoal ? other(justMoved) : justMoved,
            line: line,
            completedBy: justMoved
        };
    }
    if (isFull(board)) return { kind: 'draw', winner: null, line: null, completedBy: null };
    return null;
}

/* ──────────────── Search helpers ──────────────── */
function findImmediateWin(board, p){
    for (const i of emptyCells(board)){
        board[i] = p;
        const w = isWin(board, p);
        board[i] = null;
        if (w) return i;
    }
    return null;
}
function findForkMove(board, p){
    // Move that creates >=2 simultaneous immediate-win threats
    for (const i of emptyCells(board)){
        board[i] = p;
        let threats = 0;
        for (const j of emptyCells(board)){
            board[j] = p;
            if (isWin(board, p)) threats++;
            board[j] = null;
            if (threats >= 2) break;
        }
        board[i] = null;
        if (threats >= 2) return i;
    }
    return null;
}
function countOpenDoubles(board, p){
    // Number of lines where player has exactly 2 marks and 3rd cell is empty
    let n = 0;
    for (const line of WINNING_LINES){
        let mine = 0, empty = 0;
        for (const idx of line){
            if (board[idx] === p) mine++;
            else if (board[idx] === null) empty++;
        }
        if (mine === 2 && empty === 1) n++;
    }
    return n;
}
// Cells that do NOT complete a line for `p` — i.e. the moves that are safe to
// play when completing one loses.
function nonCompletingCells(board, p){
    const safe = [];
    for (const i of emptyCells(board)){
        board[i] = p;
        const w = isWin(board, p);
        board[i] = null;
        if (!w) safe.push(i);
    }
    return safe;
}

function pickWeighted(cells, rnd){
    const WEIGHT = [2,1,2, 1,3,1, 2,1,2]; // corners=2, edges=1, center=3
    const pool = [];
    for (const i of cells) for (let k=0;k<WEIGHT[i];k++) pool.push(i);
    return pool[rnd()*pool.length|0];
}

/* ──────────────── Bot: Rookie (never blocks, 30% rule-aware) ──────────────── */
function rookieMove(board, me, R, rnd){
    const empty = emptyCells(board);
    if (empty.length === 0) return -1;
    if (rnd() < 0.30){
        if (!R.invertGoal){
            // 30% of the time the rookie spots its own winning move.
            const win = findImmediateWin(board, me);
            if (win !== null) return win;
        } else {
            /* Misère mirror of the same 30% of rule-awareness: the rookie
               remembers that completing a line loses and steps around it. The
               branch has to be mirrored, not skipped — pointing the classic
               version at a misère board gives the rookie a 30% chance of
               resigning on the spot, which is not "easy", it is broken. */
            const safe = nonCompletingCells(board, me);
            if (safe.length > 0) return pickWeighted(safe, rnd);
        }
    }
    return pickWeighted(empty, rnd);
}

/* ──────────────── Bot: Cautious (priority rules + calibrated blunders) ──────────────── */
function cautiousMove(board, me, opp, R, rnd){
    if (!R.invertGoal){
        // 1) Take immediate win
        const win = findImmediateWin(board, me);
        if (win !== null) return win;
        // 2) Block — with 15% blunder
        const block = findImmediateWin(board, opp);
        if (block !== null && rnd() >= 0.15) return block;
        // 3) Fork detection — 50% probability of looking for forks
        if (rnd() < 0.50){
            const fork = findForkMove(board, me);
            if (fork !== null) return fork;
            const oppFork = findForkMove(board, opp);
            if (oppFork !== null) return oppFork;
        }
    } else {
        // Goal inverted: AVOID completing 3 for self; try to FORCE opponent to.
        const empty = emptyCells(board);
        const safe = nonCompletingCells(board, me);
        if (safe.length === 0){
            // Every remaining cell completes a line — forced loss, play anything.
            return empty[rnd()*empty.length|0];
        }
        // With 15% blunder skip the avoidance check (pretend-mistake)
        if (rnd() >= 0.15){
            // Among safe moves, prefer ones that leave the opponent with the
            // most cells that would complete a line for them.
            let best = -Infinity, bestCells = [safe[0]];
            for (const i of safe){
                board[i] = me;
                let oppLossLikely = 0;
                for (const j of emptyCells(board)){
                    board[j] = opp;
                    if (isWin(board, opp)) oppLossLikely++;
                    board[j] = null;
                }
                board[i] = null;
                if (oppLossLikely > best){ best = oppLossLikely; bestCells = [i]; }
                else if (oppLossLikely === best) bestCells.push(i);
            }
            return bestCells[rnd()*bestCells.length|0];
        }
        return safe[rnd()*safe.length|0];
    }

    // 4) Heuristic cell scoring (classic only, fork fallthrough)
    const BASE = [3,1,3, 1,4,1, 3,1,3];
    const empty = emptyCells(board);
    let best = -Infinity, bestCells = [empty[0]];
    for (const i of empty){
        let s = BASE[i];
        for (const line of LINES_THROUGH[i]){
            let myOnly = true;
            for (const j of line){
                if (board[j] === opp){ myOnly = false; break; }
            }
            if (myOnly) s += 2;
        }
        if (s > best){ best = s; bestCells = [i]; }
        else if (s === best) bestCells.push(i);
    }
    return bestCells[rnd()*bestCells.length|0];
}

/* ──────────────── Bot: Strategist (minimax α-β + opening book + tiebreaker) ──────────────── */
function strategistMove(board, me, opp, moveNumber, R, rnd){
    if (moveNumber === 0){
        let allEmpty = true;
        for (let i=0;i<9;i++) if (board[i] !== null){ allEmpty = false; break; }
        if (allEmpty){
            const book = R.openingBook;
            return book[rnd()*book.length|0];
        }
    }

    const empty = emptyCells(board);
    let bestScore = -Infinity, tied = [empty[0]];
    for (const i of empty){
        board[i] = me;
        const s = minimax(board, false, me, opp, -Infinity, Infinity, 1, R);
        board[i] = null;
        if (s > bestScore){ bestScore = s; tied = [i]; }
        else if (s === bestScore) tied.push(i);
    }

    if (tied.length === 1) return tied[0];

    // Tiebreaker: prefer moves that create forks / reduce opp mobility / press forward
    let bestTb = -Infinity, bestCell = tied[0];
    for (const i of tied){
        board[i] = me;
        let tb = 0;

        // Count immediate-win threats this move creates
        let threats = 0;
        const empty2 = emptyCells(board);
        for (const j of empty2){
            board[j] = me;
            if (isWin(board, me)) threats++;
            board[j] = null;
        }
        // When the goal is inverted, threats are against US — flip the sign.
        tb += (R.invertGoal ? -threats : threats) * 10;

        // Count open doubles (two-in-row)
        const doubles = countOpenDoubles(board, me);
        tb += (R.invertGoal ? -doubles : doubles) * 3;

        // Reduce opponent's good replies (fewer = better for us)
        let oppGood = 0;
        for (const j of empty2){
            board[j] = opp;
            const reply = minimax(board, true, me, opp, -Infinity, Infinity, 2, R);
            board[j] = null;
            if (reply >= 0) oppGood++;
        }
        tb -= oppGood;

        // Small random jitter to break ties softly
        tb += rnd() * 0.1;

        board[i] = null;
        if (tb > bestTb){ bestTb = tb; bestCell = i; }
    }
    return bestCell;
}

function minimax(board, maximizing, me, opp, alpha, beta, depth, R){
    const wMe = isWin(board, me);
    const wOpp = isWin(board, opp);
    if (R.invertGoal){
        // Completing 3 LOSES for the player who completed it.
        if (wMe)  return depth - 10;
        if (wOpp) return 10 - depth;
    } else {
        if (wMe)  return 10 - depth;
        if (wOpp) return depth - 10;
    }
    if (isFull(board)) return 0;

    if (maximizing){
        let best = -Infinity;
        for (let i=0;i<9;i++){
            if (board[i] !== null) continue;
            board[i] = me;
            const s = minimax(board, false, me, opp, alpha, beta, depth+1, R);
            board[i] = null;
            if (s > best) best = s;
            if (best > alpha) alpha = best;
            if (beta <= alpha) break;
        }
        return best;
    } else {
        let best = Infinity;
        for (let i=0;i<9;i++){
            if (board[i] !== null) continue;
            board[i] = opp;
            const s = minimax(board, true, me, opp, alpha, beta, depth+1, R);
            board[i] = null;
            if (s < best) best = s;
            if (best < beta) beta = best;
            if (beta <= alpha) break;
        }
        return best;
    }
}

/* Single entry point for "bot, move". `variant` — not a boolean — so the bot
   and the referee above cannot end up reading different rulebooks.
   `rnd` is injectable so tests can drive the blunder branches deterministically. */
function chooseMove(board, difficulty, me, opp, moveNumber, variant, rnd){
    const R = rulesFor(variant);
    rnd = rnd || Math.random;
    if (difficulty === 'easy')   return rookieMove(board, me, R, rnd);
    if (difficulty === 'medium') return cautiousMove(board, me, opp, R, rnd);
    return strategistMove(board, me, opp, moveNumber, R, rnd);
}

const TttRules = {
    WINNING_LINES: WINNING_LINES,
    LINES_THROUGH: LINES_THROUGH,
    VARIANTS: VARIANTS,
    rulesFor: rulesFor,
    other: other,
    isWin: isWin,
    isFull: isFull,
    emptyCells: emptyCells,
    outcome: outcome,
    chooseMove: chooseMove,
    // Exposed for tests / reuse; the page itself only needs the four above.
    findImmediateWin: findImmediateWin,
    findForkMove: findForkMove,
    countOpenDoubles: countOpenDoubles,
    nonCompletingCells: nonCompletingCells,
    minimax: minimax
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = TttRules;
} else {
    root.TttRules = TttRules;
}
})(typeof self !== 'undefined' ? self : this);
